import { useState, useEffect, useRef } from 'react';
import { MdAdd, MdSave, MdFileDownload, MdChevronLeft, MdChevronRight, MdGrading, MdSend, MdHistory } from 'react-icons/md';
import EditableTitle from './EditableTitle';
import NewDocumentModal from './NewDocumentModal';
import { documentEditorApi } from '../../utils/api/DocumentEditorApi';
import { useClass } from '../../utils/ClassContext';

const DocumentNavigationBar = ({ 
  documentTitle, 
  setDocumentTitle,
  onNewDocument = null, // New document creation function
  currentDocument = null, // Pass current document for API calls
  lastSavedTime = null, // Last saved timestamp
  onSave = null, // Save function
  onExport = null, // Export function
  tocVisible = true, // TOC visibility state
  onToggleToc = null, // TOC toggle function
  isStudent = false, // Student mode flag
  showRubric = false, // Show rubric button
  onOpenRubric = null, // Rubric button handler
  showSubmissions = false, // Show submissions history button
  onOpenSubmissions = null, // Submissions history handler
  assignmentId = null, // Assignment context
  isSubmitting = false, // Submission in progress
  onSubmitAssignment = null, // Submit handler
  dueDateStatus = { canSubmit: true, isLate: false, isLocked: false }
}) => {
  const { getClassContext } = useClass();
  const [currentTime, setCurrentTime] = useState(new Date());
  const timeoutRef = useRef(null);
  
  // New Document Modal state
  const [showNewDocumentModal, setShowNewDocumentModal] = useState(false);
  const [isCreatingDocument, setIsCreatingDocument] = useState(false);

  // Update current time for relative time display with adaptive interval
  useEffect(() => {
    if (!lastSavedTime) return;

    const updateInterval = () => {
      const diffInMs = new Date() - lastSavedTime;
      const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
      
      // Update more frequently for recent saves, less frequently for older ones
      if (diffInMinutes < 1) {
        return 1000; // Every second for "Just now"
      } else if (diffInMinutes < 60) {
        return 60000; // Every minute for minutes
      } else {
        return 300000; // Every 5 minutes for hours/days
      }
    };

    const updateTime = () => {
      setCurrentTime(new Date());
      
      // Clear previous timeout and set new one with adaptive interval
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(updateTime, updateInterval());
    };

    updateTime(); // Initial update

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [lastSavedTime]);

  // Format relative time based on time difference
  const formatRelativeTime = (savedTime) => {
    if (!savedTime) return 'Not saved yet';
    
    const diffInMs = currentTime - savedTime;
    const diffInSeconds = Math.floor(diffInMs / 1000);
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);
    
    if (diffInSeconds < 60) {
      return 'Just now';
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes} minute${diffInMinutes === 1 ? '' : 's'} ago`;
    } else if (diffInHours < 24) {
      return `${diffInHours} hour${diffInHours === 1 ? '' : 's'} ago`;
    } else {
      return `${diffInDays} day${diffInDays === 1 ? '' : 's'} ago`;
    }
  };

  // Handle title save
  const handleTitleSave = async (newTitle) => {
    if (currentDocument && currentDocument.id) {
      // API call to rename document
      try {
        await documentEditorApi.renameDocument(currentDocument.id, newTitle, getClassContext());
        setDocumentTitle(newTitle);
      } catch (error) {
        console.error('Failed to rename document:', error);
        throw error; // Re-throw to let EditableTitle handle the error
      }
    } else {
      // For new documents, just update local state
      setDocumentTitle(newTitle);
    }
  };

  // Handle new document creation
  const handleCreateDocument = async (documentData) => {
    setIsCreatingDocument(true);
    
    try {
      // Check for duplicate names first
      const existingDocs = await documentEditorApi.getDocuments(null, getClassContext());
      if (existingDocs.success && existingDocs.documents) {
        const duplicateName = existingDocs.documents.find(doc => 
          doc.title.toLowerCase() === documentData.name.toLowerCase()
        );
        if (duplicateName) {
          throw new Error(`A document with the name "${documentData.name}" already exists. Please choose a different name.`);
        }
      }

      const response = await documentEditorApi.createDocument(
        documentData.name,
        '', // empty content initially
        null, // no folder (we're using sections instead)
        documentData.sectionId,
        '', // handler (will be auto-generated)
        getClassContext()
      );
      
      if (response.success) {
        // Close modal
        setShowNewDocumentModal(false);
        
        // Call the parent's new document handler if provided
        if (onNewDocument) {
          onNewDocument({
            id: response.document.document_id,
            title: response.document.title,
            content: response.document.content || '',
            author: response.document.user.username,
            created: response.document.meta.created_at,
            modified: response.document.meta.updated_at,
            section_id: documentData.sectionId // Include section for auto-expansion
          });
        }
      } else {
        throw new Error(response.error || 'Failed to create document');
      }
    } catch (error) {
      console.error('Failed to create document:', error);
      alert(error.message || 'Failed to create document. Please try again.');
    } finally {
      setIsCreatingDocument(false);
    }
  };


  return (
    <div className="h-12 bg-gray-50/80 border-b border-gray-200/60 flex items-center px-6 flex-shrink-0">
      {/* Document Navigation */}
      <div className="flex items-center flex-1 min-w-0">
        {/* Sidebar Toggle Button */}
        {onToggleToc && (
          <>
            <button
              onClick={onToggleToc}
              className="flex items-center justify-center w-8 h-8 text-gray-600 hover:text-gray-900 hover:bg-white/60 rounded-lg transition-colors duration-200 mr-3"
              title={tocVisible ? "Hide sidebar" : "Show sidebar"}
            >
              {tocVisible ? <MdChevronLeft size={18} /> : <MdChevronRight size={18} />}
            </button>
          </>
        )}
        
        {/* Current Document Title */}
        <div className="flex-1 min-w-0">
          {isStudent ? (
            documentTitle && (
              <h2 className="text-lg font-semibold text-gray-900 truncate">
                {documentTitle}
              </h2>
            )
          ) : (
            currentDocument ? (
              <EditableTitle
                value={documentTitle}
                onSave={handleTitleSave}
                placeholder="Untitled Document"
                className="w-full"
                isDocument={true}
              />
            ) : null
          )}
        </div>
      </div>

      {/* Document Info and Actions */}
      {!isStudent && (
        <div className="flex items-center space-x-4 flex-shrink-0">
          {/* Action Buttons */}
          <div className="flex items-center space-x-2">
            {/* New Document Button */}
            <button
              onClick={() => setShowNewDocumentModal(true)}
              className="flex items-center space-x-2 px-3 py-1.5 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-white/60 rounded-lg transition-colors duration-200"
            >
              <MdAdd size={16} />
              <span>New Document</span>
            </button>
            
            {/* Save and Export Buttons */}
            {onSave && (
              <button
                onClick={onSave}
                className="flex items-center space-x-2 px-3 py-1.5 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-white/60 rounded-lg transition-colors duration-200"
              >
                <MdSave size={16} />
                <span>Save</span>
              </button>
            )}
            
            {onExport && (
              <button
                onClick={onExport}
                className="p-2 text-gray-600 hover:text-gray-800 hover:bg-white/60 rounded-lg transition-colors duration-200"
                title="Export document"
              >
                <MdFileDownload size={18} />
              </button>
            )}

            {showRubric && onOpenRubric && (
              <button
                onClick={onOpenRubric}
                className="p-2 text-amber-600 hover:text-amber-800 hover:bg-amber-50 rounded-lg transition-colors duration-200"
                title="View Rubric"
              >
                <MdGrading size={18} />
              </button>
            )}
          </div>

          {/* Last Saved Info */}
          <div className="flex items-center space-x-2 text-xs text-gray-500">
            <span>Last saved: {formatRelativeTime(lastSavedTime)}</span>
            <div className={`w-1.5 h-1.5 rounded-full ${
              lastSavedTime ? 'bg-green-500' : 'bg-gray-400'
            }`}></div>
          </div>
        </div>
      )}

      {/* Student actions: rubric + submissions history + submit */}
      {isStudent && (
        <div className="flex items-center gap-2 flex-shrink-0">
          {showRubric && onOpenRubric && (
            <button
              onClick={onOpenRubric}
              className="p-2 text-amber-600 hover:text-amber-800 hover:bg-amber-50 rounded-lg transition-colors duration-200"
              title="View Rubric"
            >
              <MdGrading size={18} />
            </button>
          )}
          {showSubmissions && onOpenSubmissions && (
            <button
              onClick={onOpenSubmissions}
              className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors duration-200"
              title="View My Submissions"
            >
              <MdHistory size={18} />
            </button>
          )}
          {assignmentId && onSubmitAssignment && (
            <button
              onClick={onSubmitAssignment}
              disabled={isSubmitting || dueDateStatus.isLocked}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors duration-200 ${
                dueDateStatus.isLocked
                  ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                  : dueDateStatus.isLate
                    ? 'bg-orange-500 hover:bg-orange-600 text-white disabled:bg-gray-400'
                    : 'bg-green-500 hover:bg-green-600 text-white disabled:bg-gray-400'
              }`}
              title={dueDateStatus.isLocked ? 'Submissions are closed' : ''}
            >
              {isSubmitting ? (
                <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white"></div>
              ) : (
                <MdSend size={14} />
              )}
              <span>
                {dueDateStatus.isLocked ? 'Closed' : dueDateStatus.isLate ? 'Submit Late' : 'Submit'}
              </span>
            </button>
          )}
        </div>
      )}

      {/* New Document Modal - Only for instructors/TAs */}
      {!isStudent && (
        <NewDocumentModal
          isOpen={showNewDocumentModal}
          onClose={() => setShowNewDocumentModal(false)}
          onConfirm={handleCreateDocument}
          isCreating={isCreatingDocument}
        />
      )}
    </div>
  );
};

export default DocumentNavigationBar;